import { pdfjs } from 'react-pdf';
import { Document, Page } from 'react-pdf';
import { useEffect, useState, useRef } from 'react';
import {
  Grid,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  Snackbar,
  Alert,
  CircularProgress,
  Box,
  Slide,
  Portal
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import SaveAltTwoToneIcon from '@mui/icons-material/SaveAltTwoTone';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FileOpenIcon from '@mui/icons-material/FileOpen';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DownloadingIcon from '@mui/icons-material/Downloading';
import downloadHelper from '../../utils/downloadHelper';

pdfjs.GlobalWorkerOptions.workerSrc = '/workers/pdf.worker.min.mjs';

const PdfViewer = ({ pdffile, width = 320 }) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('sm'));
  const containerRef = useRef();
  const [numPages, setNumPages] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [scale, setScale] = useState(1);
  const [containerWidth, setContainerWidth] = useState(width);
  const [pageNum, setPageNum] = useState(1);
  const [slideDirection, setSlideDirection] = useState('left');
  const [workerFailed, setWorkerFailed] = useState(false);
  const [hasPromiseError, setHasPromiseError] = useState(false);
  const lref = useRef();
  const src = pdffile.surl || pdffile.url || pdffile.cdn_url || pdffile.turl;
  const [downloading, setDownloading] = useState(false);
  const [touchStarted, setTouchStarted] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
  });

  useEffect(() => {
    if (typeof Promise.withResolvers !== 'function') {
      console.error('Promise.withResolvers is not supported in this environment.');
      setHasPromiseError(true);
    }
  }, []);

  useEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth || width);
    }
  }, [matches, width]);

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleTouchStart = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setTouchStarted(true);
  };

  const handleTouchEnd = (event, actionFn) => {
    event.preventDefault();
    event.stopPropagation();
    if (touchStarted && !downloading) {
      actionFn();
    }
    setTouchStarted(false);
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPageLoading(false);
  };

  const onDocumentLoadError = (error) => {
    console.error('Error loading PDF:', error);
    setPageLoading(false);
    setWorkerFailed(true);
    showSnackbar('Failed to load PDF', 'error');
  };

  const goToPreviousPage = () => {
    if (pageNum > 1) {
      setSlideDirection('right');
      setPageNum((prev) => prev - 1);
    }
  };

  const goToNextPage = () => {
    if (pageNum < numPages) {
      setSlideDirection('left');
      setPageNum((prev) => prev + 1);
    }
  };

  const zoomIn = () => {
    setScale((prev) => Math.min(prev + 0.2, 3));
    setPageLoading(true);
  };

  const zoomOut = () => {
    setScale((prev) => Math.max(prev - 0.2, 0.3));
    setPageLoading(true);
  };

  const onClickDownload = async (event) => {
    event.stopPropagation();
    setDownloading(true);
    const { message, status } = await downloadHelper(src, 'Pdf');

    showSnackbar(message, status);
    setDownloading(false);
    setTouchStarted(false);
  };

  const onClickOpen = () => {
    setTouchStarted(false);
    lref.current.click();
  };

  const buttonBaseStyle = {
    fontSize: '0.70rem',
    borderRadius: 2,
    color: 'white',
    minHeight: 28,
    minWidth: 28,
    padding: '2px 8px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    lineHeight: 1.2,
  };

  const openButtonStyle = {
    ...buttonBaseStyle,
    backgroundColor: '#0ABAB5',
    '&:hover': {
      backgroundColor: '#0ab1ac',
    },
    touchAction: 'manipulation',
    WebkitTouchCallout: 'none',
    WebkitUserSelect: 'none',
    WebkitTapHighlightColor: 'transparent',
    userSelect: 'none',
  };

  const downloadButtonStyle = {
    ...buttonBaseStyle,
    backgroundColor: '#4ea5f9',
    '&:hover': {
      backgroundColor: '#509af4',
    },
    touchAction: 'manipulation',
    WebkitTouchCallout: 'none',
    WebkitUserSelect: 'none',
    WebkitTapHighlightColor: 'transparent',
    userSelect: 'none',
  };

  const zoomButtonStyle = {
    ...buttonBaseStyle,
    backgroundColor: '#607d8b', // blue-grey
    '&:hover': {
      backgroundColor: '#455a64',
    },
  };

  return (
    <Grid container spacing={2} direction="column" alignItems="center">
      <Grid
        item
        style={{
          maxWidth: width,
          width: '100%',
          overflowX: 'auto',
          textAlign: 'center',
        }}
      >
        {workerFailed || hasPromiseError ? (
          <Grid>
            <Tooltip title={'Download'} arrow>
              <IconButton
                style={{
                  border: '1px solid #ccc',
                  borderRadius: '8px',
                  boxShadow: '1px 1px 3px #bebebe',
                  touchAction: 'manipulation',
                  WebkitTouchCallout: 'none',
                  WebkitUserSelect: 'none',
                  WebkitTapHighlightColor: 'transparent',
                  userSelect: 'none',
                }}
                onClick={(e) => {
                  setTimeout(() => {
                    if (!touchStarted) {
                      onClickDownload(e);
                    }
                  }, 50);
                }}
                onTouchStart={handleTouchStart}
                onTouchEnd={(e) => handleTouchEnd(e, () => onClickDownload(e))}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                disabled={downloading}
              >
                <InsertDriveFileIcon color="error" />
              </IconButton>
            </Tooltip>
          </Grid>
        ) : (
          <div>
            <Grid
              item
              ref={containerRef}
              sx={{
                width: '100%',
                maxWidth: width,
                overflow: 'auto',
              }}
            >
              <Document
                file={pdffile.surl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={
                  <Box height={400} display="flex" alignItems="center" justifyContent="center">
                    <CircularProgress />
                  </Box>
                }
              >
                <Box position="relative" width="100%">
                  {pageLoading && (
                    <Box
                      position="absolute"
                      top={0}
                      left={0}
                      right={0}
                      bottom={0}
                      zIndex={1}
                      bgcolor="rgba(255,255,255,0.6)"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <CircularProgress />
                    </Box>
                  )}

                  <Slide
                    direction={slideDirection}
                    in={!pageLoading}
                    mountOnEnter
                    unmountOnExit
                    timeout={400}
                    key={pageNum}
                  >
                    <Box position="relative">
                      <IconButton
                        onClick={goToPreviousPage}
                        disabled={pageNum <= 1}
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: 8,
                          transform: 'translateY(-50%)',
                          bgcolor: 'white',
                          boxShadow: 1,
                          zIndex: 10,
                        }}
                      >
                        <ArrowBackIosIcon color="primary" />
                      </IconButton>

                      <Page
                        pageNumber={pageNum}
                        width={containerWidth}
                        scale={scale}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        onRenderSuccess={() => setPageLoading(false)}
                        onRenderError={() => setPageLoading(false)}
                      />

                      <IconButton
                        onClick={goToNextPage}
                        disabled={pageNum >= numPages}
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          right: 8,
                          transform: 'translateY(-50%)',
                          bgcolor: 'white',
                          boxShadow: 1,
                          zIndex: 10,
                        }}
                      >
                        <ArrowForwardIosIcon color="primary" />
                      </IconButton>

                      {pageNum < numPages && (
                        <Box sx={{ display: 'none' }}>
                          <Page
                            pageNumber={pageNum + 1}
                            width={containerWidth}
                            scale={scale}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                          />
                        </Box>
                      )}
                      {pageNum > 1 && (
                        <Box sx={{ display: 'none' }}>
                          <Page
                            pageNumber={pageNum - 1}
                            width={containerWidth}
                            scale={scale}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                          />
                        </Box>
                      )}
                    </Box>
                  </Slide>
                </Box>
              </Document>

              {downloading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: 320,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(255,255,255,0.5)',
                    borderRadius: 8,
                    zIndex: 9999,
                  }}
                >
                  <DownloadingIcon
                    sx={{
                      fontSize: 40,
                      color: 'primary.main',
                      animation: 'pulse 1s infinite',
                      '@keyframes pulse': {
                        '0%': { opacity: 0.3 },
                        '50%': { opacity: 1 },
                        '100%': { opacity: 0.3 },
                      },
                    }}
                  />
                </Box>
              )}
            </Grid>

            <Grid
              item
              container
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              sx={{ mt: 1 }}
            >
              <Grid item>
                <Tooltip title="Open" arrow>
                  <IconButton 
                    size="small" 
                    onClick={() => {
                      setTimeout(() => {
                        if (!touchStarted) {
                          onClickOpen();
                        }
                      }, 50);
                    }}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={(e) => handleTouchEnd(e, onClickOpen)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    sx={openButtonStyle}
                  >
                    <FileOpenIcon fontSize="small" sx={{ marginRight: 0.5 }} />
                    <span style={{ fontSize: '0.70rem' }}>Open</span>
                  </IconButton>
                </Tooltip>
              </Grid>

              {/* eslint-disable-next-line jsx-a11y/anchor-has-content */}
              <a
                ref={lref}
                style={{ display: 'none' }}
                href={pdffile.surl}
                target="_blank"
                rel="noreferrer"
              />

              <Grid item>
                <Tooltip title="Download" arrow>
                  <IconButton 
                    size="small" 
                    onClick={(e) => {
                      setTimeout(() => {
                        if (!touchStarted) {
                          onClickDownload(e);
                        }
                      }, 50);
                    }}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={(e) => handleTouchEnd(e, () => onClickDownload(e))}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    disabled={downloading}
                    sx={downloadButtonStyle}
                  >
                    <SaveAltTwoToneIcon fontSize="small" sx={{ marginRight: 0.3 }} />
                    <span style={{ fontSize: '0.70rem' }}>Download</span>
                  </IconButton>
                </Tooltip>
              </Grid>

              <Grid item>
                <Tooltip title="Zoom In" arrow>
                  <IconButton size="small" onClick={zoomIn} sx={zoomButtonStyle}>
                    <ZoomInIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Grid>
              <Grid item>
                <Tooltip title="Zoom Out" arrow>
                  <IconButton size="small" onClick={zoomOut} sx={zoomButtonStyle}>
                    <ZoomOutIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </div>
        )}
      </Grid>
      <Portal>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Portal>
    </Grid>
  );
};

export default PdfViewer;